import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { SabiasQueNY } from "./nyHelpers";

const EMPIRE_SOHO_MAP_EMBED =
	"https://www.google.com/maps/d/embed?mid=1-IWPB-Liz1sRlvIR0iiUsiRJS5Nc4qI&ehbc=2E312F";

// Template86: Día 5 Mapa Empire-SoHo + Union Square, Washington, SoHo (Page16). Contenido desde page.
const Template86 = ({
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onMapFullscreenChange,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(navigator.onLine);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(navigator.onLine);

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		if (!text) return null;
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};

	const title = page?.texts?.[0]?.content;
	const intro = page?.texts?.[1]?.content;
	const unionH3 = page?.texts?.[2]?.content;
	const unionP = page?.texts?.[3]?.content;
	const sabiasQueUnion = parseSabiasQueItem(page?.texts?.[4]?.content);
	const washingtonH3 = page?.texts?.[5]?.content;
	const washingtonP = page?.texts?.[6]?.content;
	const sabiasQueWashington = parseSabiasQueItem(page?.texts?.[7]?.content);
	const sohoH3 = page?.texts?.[8]?.content;
	const sohoP = page?.texts?.[9]?.content;
	const broadwayRaw = page?.texts?.[10]?.content;
	const broadwayTitle = broadwayRaw?.includes("\n")
		? broadwayRaw.split("\n")[0]
		: broadwayRaw;
	const broadwayBody = broadwayRaw?.includes("\n")
		? broadwayRaw.split("\n").slice(1).join(" ").trim()
		: "";

	const mapUrl = page?.maps?.[0] || EMPIRE_SOHO_MAP_EMBED;
	const mapaOfflineSrc = getImageSrc("mapa-offline");
	const unionSrc = getImageSrc("union");
	const washingtonSrc = getImageSrc("washington");
	const sohoSrc = getImageSrc("soho");
	const washingtonStickerSrc = getImageSrc("washington-sticker");
	const sohoStickerSrc = getImageSrc("soho-sticker");
	const logoSrc = getCanvasImageUrl("logo");

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	if (fullscreen) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#59c3d8",
					}}
				>
					<Box
						component="button"
						onClick={() => {
							setFullscreen(false);
							onMapFullscreenChange?.(false);
						}}
						sx={{
							backgroundColor: "#fff",
							color: "#59c3d8",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: fontFamily || '"Poppins", sans-serif',
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<Box
					component="iframe"
					title="Mapa grande"
					src={mapUrl}
					width="100%"
					height="100%"
					sx={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	const estiloH3 = {
		fontFamily: '"Poppins", sans-serif',
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};
	const estiloP = {
		fontFamily: '"Poppins", sans-serif',
		fontSize: 13,
		color: "#444",
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				backgroundColor: "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": { marginTop: "0px !important", marginBottom: "0px !important" },
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: fontFamily || '"Poppins", sans-serif',
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 99999,
				}}
			>
				{title}
				{logoSrc && (
					<img
						src={logoSrc}
						alt="logo"
						style={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</div>

			<div
				data-block-id="map"
				style={{
					flex: 1,
					marginTop: "-70px",
					position: "relative",
					minHeight: 340,
					display: "flex",
					flexDirection: "column",
					minWidth: 0,
				}}
			>
				{!loaded && isOnline && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<div
							style={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: "4px solid #59c3d8",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
							}}
						/>
					</div>
				)}
				{isOnline && (
					<Box
						component="button"
						onClick={() => {
							setFullscreen(true);
							onMapFullscreenChange?.(true);
						}}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: "#59c3d8",
							color: "#fff",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

							border: "none",
							cursor: "pointer",
							fontFamily: fontFamily || '"Poppins", sans-serif',
						}}
					>
						Ver mapa grande
					</Box>
				)}
				<div style={{ flex: 1, minHeight: 0, position: "relative" }}>
					{isOnline ? (
						<Box
							component="iframe"
							title="Mapa Del Empire al Soho"
							src={mapUrl}
							width="100%"
							height="100%"
							sx={{
								border: 0,
								display: "block",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							}}
							loading="lazy"
							onLoad={() => setLoaded(true)}
						/>
					) : mapaOfflineSrc ? (
						<img
							src={mapaOfflineSrc}
							alt="Mapa Empire SoHo (offline)"
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							}}
						/>
					) : null}
				</div>
				{washingtonStickerSrc && (
					<img
						src={washingtonStickerSrc}
						alt="Washington"
						style={{ width: 100, position: "absolute", bottom: -10, left: 0 }}
					/>
				)}
			</div>

			{intro && (
				<p
					data-block-id="text-1"
					style={{
						...estiloP,
						fontSize: 13,
						color: "#333",
						padding: "10px 12px 0",
						marginBottom: 12,
					}}
				>
					{intro}
				</p>
			)}

			{(unionH3 || unionP || unionSrc) && (
				<div
					data-block-id="text-2"
					style={{
						display: "flex",
						gap: 16,
						padding: "0 12px",
						marginBottom: 16,
						position: "relative",
						marginTop: 4,
					}}
				>
					{unionSrc && (
						<img
							src={unionSrc}
							alt="Union Square"
							style={{
								width: "40%",
								height: 100,
								objectFit: "cover",
								borderRadius: 12,
							}}
						/>
					)}
					<div style={{ width: "60%" }}>
						{unionH3 && <h3 style={estiloH3}>{unionH3}</h3>}
						{unionP && <p style={estiloP}>{unionP}</p>}
					</div>
					{sabiasQueUnion && (
						<div style={{ position: "absolute", top: 40, left: "33%" }}>
							<SabiasQueNY
								icono={sabiasQueUnion.icono}
								texto={sabiasQueUnion.texto}
							/>
						</div>
					)}
				</div>
			)}

			{(washingtonH3 || washingtonP || washingtonSrc) && (
				<div
					data-block-id="text-3"
					style={{
						display: "flex",
						gap: 16,
						padding: "0 12px",
						marginBottom: 16,
						position: "relative",
					}}
				>
					{washingtonSrc && (
						<img
							src={washingtonSrc}
							alt="Washington Square Park"
							style={{
								width: "40%",
								height: 100,
								objectFit: "cover",
								borderRadius: 12,
							}}
						/>
					)}
					<div style={{ width: "60%" }}>
						{washingtonH3 && <h3 style={estiloH3}>{washingtonH3}</h3>}
						{washingtonP && <p style={estiloP}>{washingtonP}</p>}
					</div>
					{sabiasQueWashington && (
						<div style={{ position: "absolute", top: 40, left: "33%" }}>
							<SabiasQueNY
								icono={sabiasQueWashington.icono}
								texto={sabiasQueWashington.texto}
							/>
						</div>
					)}
				</div>
			)}

			{(sohoH3 || sohoP || sohoSrc) && (
				<div
					data-block-id="text-4"
					style={{
						display: "flex",
						gap: 16,
						padding: "0 12px",
						marginBottom: 16,
						position: "relative",
					}}
				>
					{sohoSrc && (
						<img
							src={sohoSrc}
							alt="SoHo"
							style={{
								width: "40%",
								height: 100,
								objectFit: "cover",
								borderRadius: 12,
							}}
						/>
					)}
					<div style={{ width: "60%" }}>
						{sohoH3 && <h3 style={estiloH3}>{sohoH3}</h3>}
						{sohoP && <p style={estiloP}>{sohoP}</p>}
					</div>
					{sohoStickerSrc && (
						<img
							src={sohoStickerSrc}
							alt="SoHo"
							style={{
								width: 80,
								position: "absolute",
								bottom: -15,
								left: -10,
							}}
						/>
					)}
					{(broadwayTitle || broadwayBody) && (
						<div
							style={{
								position: "absolute",
								bottom: 60,
								left: 2,
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 16px 0px",
								padding: "10px 12px",
								fontSize: 11,
								fontFamily: '"Poppins", sans-serif',
								color: "#444",
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: 86,
							}}
						>
							{broadwayTitle && <strong>{broadwayTitle}</strong>}
							{broadwayBody && (
								<>
									<br />
									{broadwayBody}
								</>
							)}
						</div>
					)}
				</div>
			)}
		</Box>
	);
};

export default Template86;
